import { type ReactElement, type ComponentPropsWithoutRef } from "react";
import styles from "./Button.module.css";
import Tooltip from "../Tooltip";

export type ButtonProps = {
  buttonType?: "primary" | "secondary" | "disabled" | "danger" | "green";
  hasIcon?: boolean;
  icon?: ReactElement;
  hasTooltip?: boolean;
  toolTipContent?: string;
} & ComponentPropsWithoutRef<"button">;

const Button = ({
  children,
  buttonType,
  hasIcon,
  icon,
  hasTooltip,
  toolTipContent = "",
  className = "",
  style,
  ...props
}: ButtonProps) => {
  // 1. Determine the class names based on button type
  const getButtonClasses = () => {
    const classes = [styles.button];

    if (buttonType) {
      // Map 'disabled' to 'disabled-button' class, others map directly
      const typeClassMap: Record<string, string> = {
        disabled: styles["disabled-button"],
        primary: styles.primary,
        secondary: styles.secondary,
        danger: styles.danger,
        green: styles.green,
      };
      if (typeClassMap[buttonType]) classes.push(typeClassMap[buttonType]);
    }

    if (className) classes.push(className);

    return classes.join(" ");
  };

  const isDisabled = buttonType === "disabled";
  const buttonClasses = getButtonClasses();

  // 2. Define the Button Element
  const buttonElement = (
    <button
      {...props}
      className={buttonClasses}
      style={style}
      disabled={isDisabled || props.disabled}
    >
      {hasIcon && icon}
      {children}
    </button>
  );

  // 3. Logic for Tooltip Wrapping
  if (hasTooltip) {
    return (
      <Tooltip
        content={<p dangerouslySetInnerHTML={{ __html: toolTipContent }} />}
      >
        <div
          className={className}
          style={{
            display: "inline-flex",
            width: style?.width || "auto",
            padding: 0,
            border: "none",
          }}
        >
          {buttonElement}
        </div>
      </Tooltip>
    );
  }

  return buttonElement;
};

export default Button;
